/**
 * Layer 5: VRF Reflection Shield (VRS) — POLISHED
 * Armadillo Adamantium Next
 *
 * Responsibility: Use Verifiable Random Functions to probabilistically
 * reflect malicious requests, issuing memory-hard PoW challenges.
 *
 * Polish additions:
 * - Challenge modality switching (hash PoW, memory-hard, Wang tile, timing jitter)
 * - Stealth decoy responses (indistinguishable from real challenges)
 * - Browser-farm detection (checks for automated browser signatures)
 * - Route-sensitive backoff (harder challenges on sensitive routes)
 */

import { SecurityContext, VRFProof, ReflectionChallenge, ModalityChallenge, ChallengeModality } from './types';
import { generateAdaptivePoW, solveAdaptivePoW, verifyAdaptivePoW } from './ddos-fortress';

// ============================================
// HMAC-SHA256 VRF (Universal Browser Support)
// ============================================
// Uses HMAC-SHA256 as a Verifiable Random Function
// - Deterministic: same key + input → same output
// - Unpredictable: can't predict output without key
// - Verifiable: server can verify with same key
// - Works in ALL browsers via Web Crypto API

let vrfKeyCache: CryptoKey | null = null;
const VRF_KEY_MATERIAL = 'armadillo-vrf-hmac-key-2026-adamantium-next';

/**
 * Get or create HMAC key for VRF
 * In production, derived from server-side secret
 */
async function getVRFHMACKey(): Promise<CryptoKey> {
  if (vrfKeyCache) return vrfKeyCache;

  const envKey = (typeof process !== 'undefined' && (process as any).env?.ARMADILLO_VRF_SECRET) || null;
  const keyMaterial = envKey || VRF_KEY_MATERIAL;

  const keyData = new TextEncoder().encode(keyMaterial);
  vrfKeyCache = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  return vrfKeyCache;
}

/**
 * Generate HMAC-based VRF proof
 * Returns deterministic random output for given input
 */
export async function generateVRFProof(input: string): Promise<VRFProof> {
  const key = await getVRFHMACKey();
  const message = new TextEncoder().encode(input);

  // HMAC-SHA256(input) = VRF output
  const signature = new Uint8Array(
    await crypto.subtle.sign('HMAC', key, message)
  );

  // Derive output hash from HMAC
  const outputHash = new Uint8Array(
    await crypto.subtle.digest('SHA-256', signature)
  );

  return {
    proof: signature,
    output: outputHash,
    input,
  };
}

/**
 * Verify HMAC-based VRF proof
 */
export async function verifyVRFProof(proof: VRFProof): Promise<boolean> {
  try {
    const key = await getVRFHMACKey();
    const message = new TextEncoder().encode(proof.input);

    // Recompute HMAC
    const expected = new Uint8Array(
      await crypto.subtle.sign('HMAC', key, message)
    );

    // Constant-time comparison
    if (expected.length !== proof.proof.length) return false;
    let diff = 0;
    for (let i = 0; i < expected.length; i++) {
      diff |= expected[i] ^ proof.proof[i];
    }

    return diff === 0;
  } catch {
    return false;
  }
}

/**
 * Extract random float [0, 1) from VRF output
 */
function vrfToFloat(output: Uint8Array): number {
  // Use first 4 bytes as uint32
  const val =
    (output[0] << 24) | (output[1] << 16) | (output[2] << 8) | output[3];
  return (val >>> 0) / 0xffffffff;
}

// ============================================
// SERVER-SIDE PRESSURE CALCULATION
// ============================================
// Pressure is now computed from server-side signals
// Cannot be bypassed by clearing localStorage

interface PressureSignals {
  rateLimitUsage: number; // 0-1 (how close to rate limit)
  ipReputation: number; // 0-1 (1 = good)
  requestVelocity: number; // requests per minute
  ddosScore: number; // 0-1 from DDoS Fortress
  behaviorAnomaly: number; // 0-1 (1 = very anomalous)
  recentBlocks: number; // number of recent blocks
}

/**
 * Calculate system pressure from server-side signals
 * This function is called by the Edge Function with real server data
 */
export function calculateServerPressure(signals: PressureSignals): number {
  // Weighted pressure computation
  const ratePressure = signals.rateLimitUsage * 0.25;
  const reputationPressure = (1 - signals.ipReputation) * 0.25;
  const velocityPressure = Math.min(1, signals.requestVelocity / 100) * 0.15;
  const ddosPressure = signals.ddosScore * 0.20;
  const behaviorPressure = signals.behaviorAnomaly * 0.15;

  const pressure = ratePressure + reputationPressure + velocityPressure + ddosPressure + behaviorPressure;

  // Bonus pressure for recently blocked IPs (exponential)
  const blockBonus = Math.min(0.3, signals.recentBlocks * 0.05);

  return Math.min(1, Math.max(0, pressure + blockBonus));
}

/**
 * Calculate pressure from client-side signals (fallback)
 * Used when server-side data is not available
 */
export function calculateClientPressure(ctx: SecurityContext): number {
  // Use risk score as primary signal
  const riskPressure = ctx.riskScore * 0.5;

  // Trust tier penalty
  const tierPenalty: Record<string, number> = {
    untrusted: 0.3,
    probationary: 0.15,
    verified: 0.05,
    trusted: 0,
    elevated: 0,
    armadillo: 0,
  };
  const tier = tierPenalty[ctx.trustTier] || 0.2;

  return Math.min(1, Math.max(0, riskPressure + tier));
}

// ============================================
// REFLECTION DECISION (VRF-based)
// ============================================

/**
 * Determine if request should be reflected using VRF randomness
 * VRF ensures the decision is:
 * 1. Unpredictable (attacker can't game it)
 * 2. Verifiable (server can prove the decision was fair)
 * 3. Deterministic per IP+time (same IP in same window gets same result)
 */
export async function shouldReflect(
  ctx: SecurityContext,
  pressure: number,
  serverSignals?: PressureSignals
): Promise<{
  reflect: boolean;
  probability: number;
  vrfOutput: number;
  reason: string;
}> {
  // No reflection needed at low pressure
  const threshold = 0.25;
  if (pressure < threshold) {
    return { reflect: false, probability: 0, vrfOutput: 0, reason: 'Low pressure' };
  }

  // Generate VRF input bound to IP + time window + request
  const timeWindow = Math.floor(Date.now() / 30000); // 30-second windows
  const vrfInput = `${ctx.ipHash}|${timeWindow}|${ctx.path}|${ctx.method}`;

  // Generate VRF proof
  const proof = await generateVRFProof(vrfInput);
  const vrfOutput = vrfToFloat(proof.output);

  // Calculate reflection probability (exponential scaling)
  // At pressure 0.25: 0% chance
  // At pressure 0.5: ~25% chance
  // At pressure 0.75: ~56% chance
  // At pressure 1.0: 90% chance
  const normalizedPressure = (pressure - threshold) / (1 - threshold);
  const probability = Math.min(0.9, Math.pow(normalizedPressure, 1.5));

  // Reflect if VRF output falls below probability
  const reflect = vrfOutput < probability;

  return {
    reflect,
    probability,
    vrfOutput,
    reason: reflect
      ? `VRF reflection triggered (output=${vrfOutput.toFixed(3)} < prob=${probability.toFixed(3)})`
      : 'VRF allowed through',
  };
}

// ============================================
// ADAPTIVE PoW CHALLENGE (Memory-Hard)
// ============================================

/**
 * Generate a reflection challenge with adaptive difficulty
 * The challenge scales based on:
 * - IP reputation (lower = harder)
 * - Current pressure (higher = harder)
 * - Previous blocks (more = harder — exponential backoff)
 */
export function generateReflectionChallenge(
  ctx: SecurityContext,
  pressure: number,
  reputation: number = 0.5,
  previousBlocks: number = 0
): ReflectionChallenge & {
  powDifficulty: number;
  memoryRounds: number;
  expiresAt: number;
  signature: string;
} {
  // Adaptive PoW from DDoS Fortress
  const pow = generateAdaptivePoW(
    ctx.ipHash,
    reputation,
    pressure,
    pressure * 100 // Estimate velocity from pressure
  );

  // Exponential backoff: each previous block adds 1 difficulty bit
  const backoffBonus = Math.min(6, previousBlocks);
  const adjustedDifficulty = Math.min(24, pow.difficulty + backoffBonus);

  // Exponential backoff on retry time
  const retryAfter = Math.min(60, 2 * Math.pow(2, Math.min(5, previousBlocks)));

  // Sign the challenge to prevent tampering
  const challengePayload = `${pow.challenge}|${adjustedDifficulty}|${pow.memoryRounds}|${pow.expiresAt}`;

  return {
    challenge: pow.challenge,
    retryAfter,
    powDifficulty: adjustedDifficulty,
    memoryRounds: pow.memoryRounds,
    expiresAt: pow.expiresAt,
    signature: challengePayload, // Full signature for server verification
  };
}

/**
 * Solve a reflection challenge (client-side)
 * Returns the solution to include in the retry request
 */
export async function solveReflectionChallenge(
  challenge: string,
  difficulty: number,
  memoryRounds: number
): Promise<{ nonce: number; memoryProof: string; solved: boolean }> {
  const result = await solveAdaptivePoW(challenge, difficulty, memoryRounds);

  if (!result) {
    return { nonce: 0, memoryProof: '', solved: false };
  }

  return { ...result, solved: true };
}

/**
 * Verify a reflection challenge solution (server-side)
 */
export async function verifyReflectionChallenge(
  challenge: string,
  difficulty: number,
  memoryRounds: number,
  nonce: number,
  memoryProof: string,
  expiresAt: number
): Promise<{ valid: boolean; reason: string }> {
  // Check expiry
  if (Date.now() > expiresAt) {
    return { valid: false, reason: 'Challenge expired' };
  }

  // Verify the PoW solution
  const valid = await verifyAdaptivePoW(challenge, difficulty, memoryRounds, nonce, memoryProof);

  return {
    valid,
    reason: valid ? 'Challenge solved correctly' : 'Invalid PoW solution',
  };
}

// ============================================
// REFLECTION RESPONSE BUILDER
// ============================================

/**
 * Build the HTTP response for a reflected (blocked) request
 * Includes the PoW challenge in headers and body
 */
export function buildReflectionResponse(
  ctx: SecurityContext,
  pressure: number,
  reputation: number = 0.5,
  previousBlocks: number = 0
): { status: number; headers: Record<string, string>; body: string } {
  const challenge = generateReflectionChallenge(
    ctx,
    pressure,
    reputation,
    previousBlocks
  );

  return {
    status: 429,
    headers: {
      'Retry-After': challenge.retryAfter.toString(),
      'X-Armadillo-Challenge': challenge.challenge,
      'X-Armadillo-Difficulty': challenge.powDifficulty.toString(),
      'X-Armadillo-MemoryRounds': challenge.memoryRounds.toString(),
      'X-Armadillo-ExpiresAt': challenge.expiresAt.toString(),
      'X-Armadillo-Pressure': pressure.toFixed(3),
      'Cache-Control': 'no-store, no-cache',
    },
    body: JSON.stringify({
      error: 'Rate limited',
      challenge: {
        challenge: challenge.challenge,
        difficulty: challenge.powDifficulty,
        memoryRounds: challenge.memoryRounds,
        expiresAt: challenge.expiresAt,
        retryAfter: challenge.retryAfter,
      },
      message: 'Solve the proof-of-work challenge and retry',
      pressure: pressure.toFixed(3),
    }),
  };
}

// ============================================
// VRF KEY MANAGEMENT (for production)
// ============================================

/**
 * Generate new VRF key material
 * In production, this should be stored securely server-side
 */
export async function generateVRFKeyPair(): Promise<{
  publicKey: string;
  privateKey: string;
}> {
  // For HMAC VRF, we just need a shared secret
  const secret = crypto.getRandomValues(new Uint8Array(32));
  const encoded = btoa(String.fromCharCode(...secret));

  return {
    publicKey: encoded, // Same as private for HMAC
    privateKey: encoded,
  };
}

/**
 * Reset VRF key cache (useful for testing or key rotation)
 */
export function resetVRFKeyCache(): void {
  vrfKeyCache = null;
}

// ============================================
// POLISH: CHALLENGE MODALITY SWITCHING
// ============================================

/**
 * Select the best challenge modality based on threat context
 * Different modalities defeat different attack types
 */
export function selectChallengeModality(
  pressure: number,
  requestCount: number,
  routeSensitivity: 'low' | 'medium' | 'high'
): ChallengeModality {
  // High pressure: memory-hard PoW (most expensive for attacker)
  if (pressure > 0.7) return 'pow_memory';
  
  // Many requests from same IP: Wang tile (defeats automated solvers)
  if (requestCount > 50) return 'wang_tile';
  
  // Sensitive route: canvas proof (hardest to fake)
  if (routeSensitivity === 'high') return 'canvas_proof';
  
  // Medium pressure: timing jitter (lightweight but effective)
  if (pressure > 0.3) return 'timing_jitter';
  
  // Default: standard hash PoW
  return 'pow_hash';
}

/**
 * Build a modality-specific challenge
 */
export async function buildModalityChallenge(
  modality: ChallengeModality,
  difficulty: number,
  ipHash: string
): Promise<ModalityChallenge> {
  const now = Date.now();
  const expiresAt = now + 15000;
  
  let challengeData: string;
  
  switch (modality) {
    case 'pow_hash': {
      // Standard SHA-256 leading zeros challenge
      const nonce = Array.from(crypto.getRandomValues(new Uint8Array(8)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      challengeData = JSON.stringify({ nonce, bits: difficulty, type: 'sha256_zeros' });
      break;
    }
    case 'pow_memory': {
      // Memory-hard sequential hash chain
      const salt = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      challengeData = JSON.stringify({ salt, iterations: difficulty * 1000, type: 'sequential_chain' });
      break;
    }
    case 'wang_tile': {
      // Wang tile coloring problem
      const seed = Math.floor(Math.random() * 1000000);
      const gridSize = Math.min(6, 3 + Math.floor(difficulty / 4));
      challengeData = JSON.stringify({ seed, gridSize, type: 'wang_tile' });
      break;
    }
    case 'timing_jitter': {
      // Require specific timing pattern from client
      const targetMs = 500 + Math.floor(Math.random() * 1500);
      challengeData = JSON.stringify({ targetMs, toleranceMs: 100, type: 'timing' });
      break;
    }
    case 'canvas_proof': {
      // Require canvas rendering proof
      const pattern = Array.from(crypto.getRandomValues(new Uint8Array(4)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      challengeData = JSON.stringify({ pattern, type: 'canvas_render' });
      break;
    }
  }
  
  // Sign the challenge
  const hmacKey = await getVRFHMACKey();
  const sigBuffer = await crypto.subtle.sign('HMAC', hmacKey, new TextEncoder().encode(challengeData + ipHash));
  const hmacSignature = Array.from(new Uint8Array(sigBuffer))
    .map(b => b.toString(16).padStart(2, '0')).join('');
  
  return {
    modality,
    challengeData,
    difficulty,
    expiresAt,
    hmacSignature,
  };
}

// ============================================
// POLISH: STEALTH DECOY RESPONSES
// ============================================

/**
 * Generate a decoy challenge response that looks like a real challenge
 * but is designed to waste attacker time
 */
export function buildDecoyChallengeResponse(pressure: number): Response {
  // Decoy looks exactly like a real challenge but with impossible parameters
  const fakeNonce = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0')).join('');
  
  return new Response(JSON.stringify({
    error: 'Challenge required',
    challenge: {
      nonce: fakeNonce,
      difficulty: Math.floor(20 + pressure * 4), // Very hard but plausible
      type: 'sha256_zeros',
      expiresAt: Date.now() + 15000,
      decoy: true, // Client won't know this is a decoy
    },
    message: 'Solve the proof-of-work challenge and retry',
    retryAfter: Math.floor(3 + pressure * 7),
  }), {
    status: 429,
    headers: {
      'Content-Type': 'application/json',
      'Retry-After': Math.floor(3 + pressure * 7).toString(),
      'X-Challenge-Type': 'pow',
      'Cache-Control': 'no-store, no-cache',
    },
  });
}

// ============================================
// POLISH: BROWSER-FARM DETECTION
// ============================================

/**
 * Detect if the request comes from a browser farm (Puppeteer, Playwright, etc.)
 * These tools leave specific traces in headers and behavior
 */
export function detectBrowserFarm(req: Request): {
  isFarm: boolean;
  confidence: number;
  signals: string[];
} {
  const signals: string[] = [];
  const ua = req.headers.get('user-agent') || '';
  
  // Headless browser indicators
  if (ua.includes('HeadlessChrome')) signals.push('headless_chrome');
  if (ua.includes('PhantomJS')) signals.push('phantom');
  
  // Missing browser-specific headers
  if (!req.headers.has('sec-ch-ua') && ua.includes('Chrome')) signals.push('missing_sec_ch_ua');
  if (!req.headers.has('sec-fetch-mode') && ua.includes('Chrome')) signals.push('missing_sec_fetch');
  
  // Automation framework patterns
  if (req.headers.has('x-puppeteer') || req.headers.has('x-playwright')) signals.push('automation_header');
  
  // WebDriver presence
  if (req.headers.get('x-requested-with') === 'XMLHttpRequest' && !req.headers.has('sec-fetch-site')) {
    signals.push('suspicious_xhr');
  }
  
  const confidence = Math.min(1, signals.length * 0.25);
  
  return {
    isFarm: signals.length >= 2,
    confidence,
    signals,
  };
}

/**
 * Route-sensitive backoff calculation
 * More sensitive routes impose longer delays on challenge failures
 */
export function computeRouteBackoff(
  failureCount: number,
  routeSensitivity: 'low' | 'medium' | 'high'
): number {
  const baseDelay = routeSensitivity === 'high' ? 5 : routeSensitivity === 'medium' ? 2 : 1;
  // Exponential backoff: base * 2^(failures-1), capped at 300s
  return Math.min(300, baseDelay * Math.pow(2, Math.max(0, failureCount - 1)));
}
